import { Component, ErrorInfo, ReactNode } from "react";
import { ErrorPage } from "../ErrorPage";

/** The error boundary state. */
interface ModuleErrorBoundaryState {
  readonly error: Error | null;
}

/** The `ModuleErrorBoundary` component properties. */
export interface ModuleErrorBoundaryProps {

  /** The component children. */
  readonly children?: ReactNode;
}

/** Provides shell-level error handling for modules. */
export class ModuleErrorBoundary extends Component<ModuleErrorBoundaryProps, ModuleErrorBoundaryState> {

  /**
   * Derives the state for the error boundary from the specified error.
   * @param error The error to derive state from.
   */
  static getDerivedStateFromError(error: Error): ModuleErrorBoundaryState {
    return { error };
  }

  /**
   * Called when the component catches an error.
   * @param error The error being caught.
   * @param errorInfo The error information.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.log(error, errorInfo);
  }

  /** Renders the component. */
  render(): ReactNode {
    const { children } = this.props;
    const { error } = this.state;

    if (error !== null) {
      return <ErrorPage />;
    }

    return <>{children}</>;
  }

  /**
   * Initializes a new instance of the ModuleErrorBoundary class.
   * @param props The component properties.
   */
  constructor(props: {}) {
    super(props);
    this.state = { error: null };
  }
}
